import { Alert, Box, CircularProgress, List, ListItemText } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { ApiError, DetailView } from '@pro4-erp/core-web';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import Form from '../../components/Form';
import Files from './Files';
import { getMessageFromError } from '@/core';
import { useDetailPageStore } from './useDetailPageStore';
import { getAuditorVisitRepReviewById } from '../../api/getAuditorVisitRepReviewById';
import { AuditVisitRepReviewDetail } from '../../types';
import { updateAuditorVisitRepReview } from '../../api/updateAuditorVisitRepReview';

const DetailPage = () => {
  const { id } = useParams<{ id: string }>() as { id: string };

  const detailPageStore = useDetailPageStore();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const auditorVisitRepReviewQuery = useQuery<AuditVisitRepReviewDetail, ApiError>(
    ['audit-visit-rep-review-id', id],
    () => getAuditorVisitRepReviewById(id),
    { refetchOnWindowFocus: false }
  );

  useEffect(() => {
    return () => {
      detailPageStore.reset();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!auditorVisitRepReviewQuery.data) {
      return;
    }

    detailPageStore.setForm({
      note: auditorVisitRepReviewQuery.data.note || '',
      clientName: auditorVisitRepReviewQuery.data.clientName || '',
      reviewer: auditorVisitRepReviewQuery.data.reviewer || '',
      status: auditorVisitRepReviewQuery.data.status || null,
      visitReportUid: auditorVisitRepReviewQuery.data.visitReportUid || '',
      visitUid: auditorVisitRepReviewQuery.data.visituId || '',
      workTime: auditorVisitRepReviewQuery.data.workTime || 0,
    });
    detailPageStore.setIsLoading(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditorVisitRepReviewQuery.data]);

  const { mutate } = useMutation({
    mutationFn: updateAuditorVisitRepReview,
    onSuccess: () => {
      enqueueSnackbar(
        t('Successfully saved changes.', { defaultValue: 'Successfully saved changes.' }),
        { variant: 'success' }
      );
      queryClient.invalidateQueries(['auditor-competences-id', id]);
      navigate('/audit-visit-rep-review');
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  if (auditorVisitRepReviewQuery.isLoading || detailPageStore.isLoading) {
    return (
      <Box>
        <Box
          sx={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress size={100} />
        </Box>
      </Box>
    );
  }

  if (auditorVisitRepReviewQuery.isError) {
    return <p>{auditorVisitRepReviewQuery.error.message}</p>;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <DetailView
        childrenSx={{ padding: '2rem' }}
        title={
          <DetailView.Title>
            {auditorVisitRepReviewQuery.data.visitReportUid || 'N/A'}
          </DetailView.Title>
        }
        onGoBack={() => navigate('/audit-visit-rep-review')}
        tabs={[
          { id: 'details', title: t('Details', { defaultValue: 'Details' }) },
          { id: 'files', title: t('Files', { defaultValue: 'Files' }) },
        ]}
        defaultTab="details"
      >
        {({ TabContent }) => (
          <>
            <TabContent tab="details">
              {auditorVisitRepReviewQuery.data.activeReviewers &&
                auditorVisitRepReviewQuery.data.activeReviewers?.length > 0 && (
                  <Alert severity="warning">
                    <List
                      subheader={t('ReportOnReview', {
                        defaultValue: 'The report is currently being reviewed by',
                      })}
                    >
                      {auditorVisitRepReviewQuery.data.activeReviewers?.map((item) => (
                        <ListItemText
                          primaryTypographyProps={{ fontSize: '1.25rem' }}
                          secondaryTypographyProps={{ fontSize: '1rem' }}
                          key={item}
                          primary={item}
                        />
                      ))}
                    </List>
                  </Alert>
                )}
              <Form
                onSubmit={(data) =>
                  mutate({
                    id: auditorVisitRepReviewQuery.data.id,
                    data: {
                      ...data,
                    },
                  })
                }
                initialState={detailPageStore.form}
                onUnmount={(form) => detailPageStore.setForm({ ...form })}
                isFormEditable
              />
            </TabContent>
            <TabContent tab="files">
              {auditorVisitRepReviewQuery.data.activeReviewers &&
                auditorVisitRepReviewQuery.data.activeReviewers?.length > 0 && (
                  <Alert severity="warning">
                    <List
                      subheader={t('ReportOnReview', {
                        defaultValue: 'The report is currently being reviewed by',
                      })}
                    >
                      {auditorVisitRepReviewQuery.data.activeReviewers?.map((item) => (
                        <ListItemText
                          primaryTypographyProps={{ fontSize: '1.25rem' }}
                          secondaryTypographyProps={{ fontSize: '1rem' }}
                          key={item}
                          primary={item}
                        />
                      ))}
                    </List>
                  </Alert>
                )}
              <Files auditorVisitRepReview={auditorVisitRepReviewQuery.data} />
            </TabContent>
          </>
        )}
      </DetailView>
    </Box>
  );
};

export default DetailPage;
